import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PRODUCTION_HOSTNAME, SITE_NAME, SOCIAL_IMAGE, buildStructuredData, canonicalUrl, findRouteSeo, notFoundSeo } from '../lib/seo';
import { setCanonical, setJsonLd, setMetaName, setMetaProperty, setTitle } from '../utils/head';

/**
 * Keeps title, description, canonical, robots, Open Graph, Twitter and JSON-LD
 * in sync with the current route, including on client-side navigation.
 * Renders nothing.
 */
export function RouteSeo() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const matched = findRouteSeo(pathname);
    const route = matched ?? notFoundSeo;
    const isProduction = window.location.hostname === PRODUCTION_HOSTNAME;

    // Previews, drafts and any non-production host are never indexed.
    const robots = !isProduction ? 'noindex, nofollow' : route.indexable ? 'index, follow' : 'noindex, follow';
    setTitle(route.title);
    setMetaName('description', route.description);
    setMetaName('robots', robots);

    // Unknown paths get no canonical of their own, so they never claim a real URL.
    const canonicalLinks = document.head.querySelectorAll('link[rel="canonical"]');
    if (matched) {
      const url = canonicalUrl(matched.path);
      setCanonical(url);
      setMetaProperty('og:url', url);
    } else {
      canonicalLinks.forEach(node => node.remove());
      document.head.querySelectorAll('meta[property="og:url"]').forEach(node => node.remove());
    }
    setMetaProperty('og:type', 'website');
    setMetaProperty('og:site_name', SITE_NAME);
    setMetaProperty('og:title', route.title);
    setMetaProperty('og:description', route.description);
    setMetaProperty('og:image', SOCIAL_IMAGE.src);
    setMetaProperty('og:image:secure_url', SOCIAL_IMAGE.src);
    setMetaProperty('og:image:type', SOCIAL_IMAGE.type);
    // Legacy host-provided image hints could point at a different image. Remove them.
    document.head.querySelectorAll('meta[name="twitter:image:src"], meta[property="twitter:image:src"], link[rel="image_src"]').forEach(node => node.remove());
    setMetaProperty('og:image:width', String(SOCIAL_IMAGE.width));
    setMetaProperty('og:image:height', String(SOCIAL_IMAGE.height));
    setMetaProperty('og:image:alt', SOCIAL_IMAGE.alt);
    setMetaProperty('og:locale', 'en_US');
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', route.title);
    setMetaName('twitter:description', route.description);
    setMetaName('twitter:image', SOCIAL_IMAGE.src);
    setMetaName('twitter:image:alt', SOCIAL_IMAGE.alt);
    if (matched) {
      setJsonLd('page', buildStructuredData(matched));
    } else {
      document.head.querySelectorAll('script[data-seo="page"]').forEach(node => node.remove());
    }
  }, [pathname]);
  return null;
}